import { formatCalendarDate, formatInstantDay, formatInstantTime } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import LoadStatusBadge from "./LoadStatusBadge";

interface BolDocumentProps {
  bol: {
    id?: string;
    bol_number: string;
    status: string;
    load_type?: string;
    scheduled_date?: string;
    pickup_location?: string | null;
    delivery_location?: string;
    departed_at?: string;
    arrived_at?: string;
    delivered_at?: string;
    signed_at?: string;
    billing_type?: string;
    flat_haul_rate?: number;
    hourly_rate?: number;
    billable_hours?: number;
    km_driven?: number;
    rate_per_km?: number;
    pdf_url?: string | null;
    receiver_name?: string | null;
    receiver_signature?: string | null;
    driver_signature?: string | null;
    truck_unit_number?: string;
    truck_make?: string;
    truck_model?: string;
    customer_name?: string;
    customer_email?: string;
    customer_phone?: string;
    driver_name?: string;
    driver_phone?: string;
    driver_email?: string;
    fleet_unit_number?: string;
    fleet_make?: string;
    fleet_model?: string;
    /**
     * GET /trucking/loads/bols/:id builds these with a drizzle select, so the
     * keys are the schema's camelCase: `quantity` and `weightKg` (a NUMERIC,
     * so a string). This read `pieces` and `weight`, which the route has never
     * sent, so every row printed a dash and the total row "0 items" and "0".
     */
    cargo_items?: Array<{
      description: string;
      quantity?: number | null;
      weightKg?: string | number | null;
      condition?: string | null;
    }>;
  };
  companyName?: string;
}

/**
 * A signature box draws the image the load stores, or says there is none.
 *
 * It used to draw a hard-coded SVG squiggle whenever the field was set, so every
 * signed BOL showed the same made-up signature, on screen and on paper, while
 * the API's PDF embedded the real one (#803). The field holds the signing
 * canvas's PNG as a `data:image/` URL (DriverBOLScreen, DriverLoadScreen), and
 * the API's PDF puts that same value in an <img>, so the two now agree.
 *
 * Three states, never two: an image draws; nothing stored reads "Not signed";
 * a value that is set but is not an image says so rather than drawing
 * something in its place. No state ever draws a stand-in signature.
 */
function StoredSignature({ value, who }: { value?: string | null; who: string }) {
  if (!value) {
    return <p className="text-[11px] text-muted-foreground italic w-full text-center pb-1">Not signed</p>;
  }
  if (!value.startsWith("data:image/")) {
    return <p className="text-[11px] text-muted-foreground italic w-full text-center pb-1">Signed — no image on file</p>;
  }
  return <img src={value} alt={`${who} signature`} className="max-h-12 max-w-full object-contain" />;
}

/*
 * The status stamp on the printed BOL comes from the shared load_status map
 * (lib/load-status.ts), like every other trucking screen. This document leaves
 * the building, so it is the worst place to guess: the old local map had no
 * booked, en_route, arrived or cancelled and fell back to "Draft", so a
 * cancelled load printed as a Draft BOL. An unrecognised status prints "—".
 */

/**
 * `scheduled_date` is a calendar day, and this is the BOL the customer signs.
 * Parsed with `new Date()` it was UTC midnight, so a load scheduled for Jul 14
 * printed "Monday, July 13, 2026" on the delivery document.
 *
 * formatTimestamp below treats `departed_at` / `arrived_at` as the real
 * instants they are — their time of day, on the tenant's clock, is what the
 * BOL is attesting to.
 */
function formatDate(dateStr?: string): string {
  return (
    formatCalendarDate(dateStr, { weekday: "long", year: "numeric", month: "long", day: "numeric" })
    ?? (dateStr || "—")
  );
}

/**
 * An instant on the customer's BOL, read on the TENANT's clock — the dispatcher's
 * laptop or the driver's phone is not where the load was. No zone, or an
 * unparseable value, prints "—" rather than a guessed time.
 */
function formatTimestamp(dateStr: string | null | undefined, timeZone: string | null): string {
  if (!dateStr) return "—";
  const time = formatInstantTime(dateStr, timeZone, { hour: "2-digit", minute: "2-digit", hour12: false });
  const date = formatInstantDay(dateStr, timeZone, { month: "short", day: "numeric" });
  if (!time || !date) return "—";
  return `${time} — ${date}`;
}

function computeDriveTime(departed?: string | null, arrived?: string | null): string {
  if (!departed || !arrived) return "—";
  try {
    const ms = new Date(arrived).getTime() - new Date(departed).getTime();
    if (ms <= 0 || isNaN(ms)) return "—";
    const totalMin = Math.round(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return h > 0 ? `${h}h ${m}min` : `${m}min`;
  } catch {
    return "—";
  }
}

const BolDocument = ({ bol, companyName }: BolDocumentProps) => {
  const timeZone = useTenantTimeZone();
  // An absent name is a dash, never a stand-in word printed as the carrier.
  const company = companyName || "—";

  const cargoItems = bol.cargo_items ?? [];
  // All-or-nothing: one row with no figure makes the total unknown, not smaller.
  const num = (v: unknown): number | null => {
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  const sumAll = (vals: (number | null)[]) =>
    vals.length > 0 && vals.every((v) => v !== null) ? (vals as number[]).reduce((a, b) => a + b, 0) : null;
  const totalPieces = sumAll(cargoItems.map((c) => num(c.quantity)));
  const totalWeight = sumAll(cargoItems.map((c) => num(c.weightKg)));

  /*
   * THE CARRIER'S UNIT IS THE LOAD'S TRUCK, NEVER THE DRIVER'S USUAL ONE.
   * `fleet_unit_number` / `fleet_make` / `fleet_model` were a join on the unit
   * the DRIVER is assigned to (trucking_fleet.assigned_driver_id), not on the
   * load, and this printed `fleet_unit_number` first, beside the load truck's
   * make and model: BOL 2026-0006 read "Truck: H-106" above "Fleet: H-101 ·
   * Freightliner 122SD". api#1109 repointed the `fleet_*` keys at the load's
   * own truck, so today they equal `truck_*`; this reads `truck_*` all the
   * same, the key that has always meant the load's truck, with no fallback to
   * the other: a load with no truck reads a dash (fix/trucking-figures-agree).
   */
  const fleetLabel = bol.truck_unit_number || "—";
  const truckDesc = [bol.truck_make, bol.truck_model].filter(Boolean).join(" ");

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden shadow-lg print:shadow-none max-w-3xl mx-auto">
      {/* Letterhead */}
      <div className="px-8 py-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2.5 h-2.5 rounded-full bg-primary" />
            <span className="text-lg font-bold text-primary">FieldIQ</span>
          </div>
          <p className="text-xs text-muted-foreground">{company}</p>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-primary italic">Bill of Lading</p>
          <p className="text-sm font-bold text-foreground font-mono">{bol.bol_number}</p>
          <p className="text-xs text-muted-foreground">{formatDate(bol.scheduled_date)}</p>
          <div className="mt-1 flex justify-end">
            <LoadStatusBadge status={bol.status} />
          </div>
        </div>
      </div>

      <div className="border-t border-border" />

      {/* Three-party block */}
      <div className="px-8 py-5 grid grid-cols-3 gap-4">
        {/* Shipper */}
        <div className="space-y-1.5">
          <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Shipper</p>
          <p className="text-sm font-bold text-foreground">{company}</p>
          <p className="text-[11px] text-muted-foreground">Driver: {bol.driver_name || "—"}</p>
          <p className="text-[11px] text-muted-foreground">Truck: {bol.truck_unit_number || "—"}</p>
        </div>

        {/* Consignee */}
        <div className="space-y-1.5">
          <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Consignee / Operator</p>
          <p className="text-sm font-bold text-foreground">{bol.customer_name || "—"}</p>
          <p className="text-[11px] text-muted-foreground">
            Contact: {bol.customer_email || bol.customer_phone || "—"}
          </p>
        </div>

        {/* Carrier */}
        <div className="space-y-1.5">
          <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Carrier</p>
          <p className="text-sm font-bold text-foreground">{company}</p>
          <p className="text-[11px] text-muted-foreground">Fleet: {fleetLabel}</p>
          {truckDesc && <p className="text-[11px] text-muted-foreground">{truckDesc}</p>}
        </div>
      </div>

      <div className="border-t border-border" />

      {/* Route block */}
      <div className="px-8 py-4">
        <div className="bg-secondary/50 rounded-lg border border-border px-5 py-3 flex items-center gap-4">
          <div className="flex-1">
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Pickup Location</p>
            {/* The load's own pickup. This read "Yard" on every BOL whatever the
              * load said (#803); a load with no pickup recorded prints a dash. */}
            <p data-testid="bol-pickup" className="text-sm font-bold text-foreground font-mono mt-0.5">{bol.pickup_location || "—"}</p>
          </div>
          <span className="text-muted-foreground text-lg">&rarr;</span>
          <div className="flex-1 text-right">
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">Delivery Destination</p>
            <p className="text-sm font-bold text-foreground font-mono mt-0.5">{bol.delivery_location || "—"}</p>
          </div>
        </div>
      </div>

      {/* Cargo table */}
      <div className="px-8 pb-4">
        <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Cargo Description</p>
        {cargoItems.length > 0 ? (
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-secondary/60 border-b border-border">
                  <th className="text-left px-3 py-2 font-bold text-muted-foreground uppercase text-[9px] tracking-wider">Description</th>
                  <th className="text-right px-3 py-2 font-bold text-muted-foreground uppercase text-[9px] tracking-wider">Pieces / Units</th>
                  <th className="text-right px-3 py-2 font-bold text-muted-foreground uppercase text-[9px] tracking-wider">Weight (kg)</th>
                  <th className="text-right px-3 py-2 font-bold text-muted-foreground uppercase text-[9px] tracking-wider">Condition</th>
                </tr>
              </thead>
              <tbody>
                {cargoItems.map((item, i) => (
                  <tr key={i} className="border-b border-border/50">
                    <td className="px-3 py-2.5 text-foreground">{item.description}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-foreground">{num(item.quantity)?.toLocaleString() ?? "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold text-foreground">{num(item.weightKg)?.toLocaleString() ?? "—"}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-success">{item.condition ?? "—"}</td>
                  </tr>
                ))}
                <tr className="bg-secondary/30">
                  <td className="px-3 py-2.5 font-semibold text-foreground">Total</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold text-foreground">{totalPieces == null ? "—" : `${totalPieces.toLocaleString()} items`}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold text-foreground">{totalWeight == null ? "—" : totalWeight.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-success">—</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="border border-border rounded-lg p-4 text-center">
            <p className="text-xs text-muted-foreground">No cargo items recorded</p>
          </div>
        )}
      </div>

      {/* Load details grid */}
      <div className="px-8 pb-4">
        <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Load Details</p>
        <div className="bg-secondary/50 rounded-lg border border-border overflow-hidden">
          <div className="grid grid-cols-2">
            {[
              { label: "Departed Pickup", value: formatTimestamp(bol.departed_at, timeZone) },
              { label: "Arrived Destination", value: formatTimestamp(bol.arrived_at, timeZone) },
              { label: "Drive Time", value: computeDriveTime(bol.departed_at, bol.arrived_at) },
            ].map((detail, i) => (
              <div key={i} className="px-4 py-2.5 border-b border-border/50 last:border-b-0">
                <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-wider">{detail.label}</p>
                <p className="text-xs font-semibold mt-0.5 text-foreground">{detail.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Signature blocks, kept whole on one printed page. Two parties sign a
        * load: the driver at pickup and the receiver at delivery. A third box,
        * a dispatcher countersign, read "Pending dispatcher review" on every BOL
        * with nothing behind it: no column, route or screen records one (#803). */}
      <div className="px-8 pb-6 break-inside-avoid">
        <div className="grid grid-cols-2 gap-3">
          {/* Driver */}
          <div role="group" aria-label="Driver signature at pickup" className={`rounded-lg p-4 border ${bol.driver_signature ? "bg-success/10 border-success/30" : "bg-secondary border-border"}`}>
            <p className={`text-[9px] font-bold uppercase tracking-wider mb-3 ${bol.driver_signature ? "text-success" : "text-muted-foreground"}`}>
              Driver — At Pickup
            </p>
            <div className={`h-12 border-b mb-2 flex items-end ${bol.driver_signature ? "border-success/30" : "border-border"}`}>
              <StoredSignature value={bol.driver_signature} who="Driver" />
            </div>
            <p className={`text-xs font-semibold ${bol.driver_signature ? "text-success" : "text-foreground"}`}>
              {bol.driver_name || "—"} — Driver
            </p>
            <p className="text-[11px] text-muted-foreground">
              {bol.departed_at ? formatTimestamp(bol.departed_at, timeZone) : "—"}
            </p>
          </div>

          {/* Receiver */}
          <div role="group" aria-label="Receiver signature at delivery" className={`rounded-lg p-4 border ${bol.receiver_signature ? "bg-success/10 border-success/30" : "bg-secondary border-border"}`}>
            <p className={`text-[9px] font-bold uppercase tracking-wider mb-3 ${bol.receiver_signature ? "text-success" : "text-muted-foreground"}`}>
              Receiver — At Delivery
            </p>
            <div className={`h-12 border-b mb-2 flex items-end ${bol.receiver_signature ? "border-success/30" : "border-border"}`}>
              <StoredSignature value={bol.receiver_signature} who="Receiver" />
            </div>
            <p className={`text-xs font-semibold ${bol.receiver_signature ? "text-success" : "text-foreground"}`}>
              {bol.receiver_name || "—"}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {bol.arrived_at ? formatTimestamp(bol.arrived_at, timeZone) : "—"}
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border px-8 py-4">
        <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
          This Bill of Lading is the contract between shipper and carrier. Receipt of goods is acknowledged at time of driver signature. Dispute resolution per MSA terms. Document generated by FieldIQ &middot; field-iq.ai
        </p>
      </div>
    </div>
  );
};

export default BolDocument;
